import { IonBackButton, IonButton, IonButtons, IonCard, IonCardContent, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonModal, IonPage, IonRow, IonSearchbar, IonSelect, IonSelectOption, IonSpinner, IonTitle, IonToolbar } from '@ionic/react';
import React, { useState, useCallback } from 'react';
import { addCircleOutline, peopleOutline, closeOutline, constructOutline, personAddOutline, searchOutline } from 'ionicons/icons';
import EnvironmentBadge from '../../components/EnvironmentBadge';
import { useScannerBridge } from '../../hooks/useScannerBridge';
import getApi from '../../api';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom';

const PumpsMenu: React.FC = () => {
    const history = useHistory();
    const [scannerActive, setScannerActive] = useState(false);
    const [scannerLoading, setScannerLoading] = useState(false);
    const [scannerMode, setScannerMode] = useState<'router' | 'pump'>('router');
    const [showPumpModal, setShowPumpModal] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [pumpData, setPumpData] = useState({ mqttID: '', name: '', system: '' });
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [customerData, setCustomerData] = useState({
        name: '',
        email: '',
        company_name: '',
        address: '',
        zip: '',
        city: '',
        country: 'Sverige',
        org_number: '',
        mobile: '',
        is_active: true,
        vattna: true
    });

    const formatResponse = (data: any) => {
        if (!data || typeof data !== 'object') return String(data);
        const dataCopy = { ...data };

        const labels: { [key: string]: string } = {
            hmi: 'HMI',
            hmi_ip: 'HMI',
            plc: 'PLC',
            plc_ip: 'PLC',
            router: 'Router',
            router_ip: 'Router',
            dhcp_start: 'DHCP Start',
            dhcp_end: 'DHCP Slut',
            dhcp_slut: 'DHCP Slut',
            id: 'ID',
            mqttID: 'ID',
            name: 'Namn',
            netmask: 'Nätmask',
            nätmask: 'Nätmask'
        };

        const lines: string[] = [];
        // Extract known fields in a specific order if possible
        const orderedKeys = [
            ['hmi', 'hmi_ip'],
            ['plc', 'plc_ip'],
            ['router', 'router_ip'],
            ['dhcp_start'],
            ['dhcp_end', 'dhcp_slut'],
            ['id', 'mqttID'],
            ['name'],
            ['netmask', 'nätmask']
        ];

        const seenLabels = new Set();

        orderedKeys.forEach(group => {
            for (const key of group) {
                if (dataCopy[key] !== undefined && dataCopy[key] !== null) {
                    const label = labels[key];
                    if (!seenLabels.has(label)) {
                        lines.push(`<b>${label}:</b> ${dataCopy[key]}`);
                        seenLabels.add(label);
                    }
                    delete dataCopy[key];
                    break;
                }
            }
        });

        // Add remaining fields
        Object.entries(dataCopy).forEach(([key, value]) => {
            if (value !== undefined && value !== null && typeof value !== 'object') {
                lines.push(`<b>${key}:</b> ${value}`);
            }
        });

        return lines.join('<br>');
    };

    const handleScan = useCallback(async (code: string) => {
        if (!code) return;

        setScannerActive(false);

        if (scannerMode === 'router') {
            Swal.fire({
                title: 'Validerar...',
                text: `Serienummer: ${code}`,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                },
                heightAuto: false
            });

            try {
                const response = await getApi().post('/inhand/validate_serial/', { sn: code });

                if (response.data === 'ok') {
                    Swal.fire({
                        title: 'Skapar router...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            Swal.showLoading();
                        },
                        heightAuto: false
                    });

                    try {
                        const createResponse = await getApi().post('/api/bbe/createuserpumps/', {
                            new: true,
                            sn: code
                        });

                        Swal.fire({
                            title: 'Klart!',
                            html: formatResponse(createResponse.data),
                            icon: 'success',
                            confirmButtonText: 'OK',
                            heightAuto: false
                        });
                    } catch (createError: any) {
                        console.error('Create error:', createError);
                        Swal.fire({
                            title: 'Fel vid skapande!',
                            text: createError.response?.data?.error || createError.response?.data?.message || 'Kunde inte skapa routern.',
                            icon: 'error',
                            confirmButtonText: 'Stäng',
                            heightAuto: false
                        });
                    }
                } else {
                    Swal.fire({
                        title: 'Resultat',
                        text: JSON.stringify(response.data),
                        icon: 'info',
                        confirmButtonText: 'OK',
                        heightAuto: false
                    });
                }
            } catch (error: any) {
                console.error('Validation error:', error);
                Swal.fire({
                    title: 'Fel!',
                    text: error.response?.data?.error || error.response?.data?.message || 'Kunde inte validera serienumret.',
                    icon: 'error',
                    confirmButtonText: 'Stäng',
                    heightAuto: false
                });
            }
        } else {
            // Mode is 'pump'
            setPumpData({ mqttID: code, name: '', system: '' });
            setShowPumpModal(true);
        }
    }, [scannerMode]);

    const handleCreatePump = async () => {
        if (!pumpData.name || !pumpData.system) {
            Swal.fire({
                title: 'Fel!',
                text: 'Vänligen fyll i alla fält.',
                icon: 'error',
                heightAuto: false
            });
            return;
        }

        Swal.fire({
            title: 'Sparar...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
            heightAuto: false
        });

        try {
            const response = await getApi().post('/api/bbe/createuserpumps/', pumpData);
            if (response.status === 201 || response.status === 200) {
                Swal.fire({
                    title: 'Pumpen har skapats!',
                    html: formatResponse(response.data),
                    icon: 'success',
                    heightAuto: false
                });
                setShowPumpModal(false);
            }
        } catch (error: any) {
            console.error('Create pump error:', error);
            Swal.fire({
                title: 'Fel!',
                text: error.response?.data?.message || 'Kunde inte skapa pumpen.',
                icon: 'error',
                heightAuto: false
            });
        }
    };

    const handleCreateCustomer = async () => {
        if (!customerData.name || !customerData.email) {
            Swal.fire({
                title: 'Fel!',
                text: 'Namn och Email är obligatoriska.',
                icon: 'error',
                heightAuto: false
            });
            return;
        }

        Swal.fire({
            title: 'Sparar...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            },
            heightAuto: false
        });

        try {
            const response = await getApi().post('/api/bbe/customers/', customerData);
            if (response.status === 201 || response.status === 200) {
                Swal.fire({
                    title: 'Kunden har skapats!',
                    icon: 'success',
                    heightAuto: false
                });
                setShowCustomerModal(false);
                setCustomerData({
                    name: '',
                    email: '',
                    company_name: '',
                    address: '',
                    zip: '',
                    city: '',
                    country: 'Sverige',
                    org_number: '',
                    mobile: '',
                    is_active: true,
                    vattna: true
                });
            }
        } catch (error: any) {
            console.error('Create customer error:', error);
            Swal.fire({
                title: 'Fel!',
                text: error.response?.data?.email || error.response?.data?.message || 'Kunde inte skapa kunden.',
                icon: 'error',
                heightAuto: false
            });
        }
    };

    const handleSearch = async (query: string | null | undefined) => {
        const safeQuery = query || '';
        setSearchQuery(safeQuery);
        if (safeQuery.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const response = await getApi().get(`/bbe/userpumps/?search=${encodeURIComponent(safeQuery)}`);
            setSearchResults(response.data.results || []);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsSearching(false);
        }
    };

    const { startScanner, stopScanner } = useScannerBridge({ autoStart: false, onScan: handleScan });

    const handleActivateScanner = async () => {
        setScannerActive(true);
        setScannerLoading(true);

        try {
            await startScanner();
            setScannerLoading(false);
        } catch (error) {
            console.error('Failed to start scanner:', error);
            setScannerActive(false);
            setScannerLoading(false);
            Swal.fire({
                title: 'Fel!',
                text: 'Kunde inte starta skannern. Kontrollera kamerabehörigheter.',
                icon: 'error',
                heightAuto: false
            });
        }
    };

    const handleStopScanner = () => {
        stopScanner();
        setScannerActive(false);
        setScannerLoading(false);
    };

    return (
        <IonPage placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonButtons slot="start" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        {scannerActive ? (
                            <IonButton onClick={handleStopScanner} color="light" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonIcon slot="icon-only" icon={closeOutline} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                            </IonButton>
                        ) : (
                            <IonBackButton />
                        )}
                    </IonButtons>
                    <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        {scannerActive ? 'Skanna enhet' : 'Pumpar'}
                    </IonTitle>
                    <div slot="end" style={{ paddingRight: '8px' }}><EnvironmentBadge /></div>
                </IonToolbar>
            </IonHeader>
            <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                <div
                    id="data-capture-view"
                    style={{ position: 'fixed', inset: 0, zIndex: 20, pointerEvents: 'none', display: scannerActive ? 'block' : 'none' }}
                />

                {scannerActive && scannerLoading && (
                    <div style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 21,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        pointerEvents: 'none'
                    }}>
                        <div style={{ textAlign: 'center', color: 'white' }}>
                            <IonSpinner name="crescent" style={{ width: '48px', height: '48px' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                            <div style={{ marginTop: '16px', fontSize: '16px' }}>Startar skanner...</div>
                        </div>
                    </div>
                )}

                <IonGrid placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonCol size="6" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard onClick={() => { setScannerMode('router'); handleActivateScanner(); }} className="home-card" style={{ cursor: 'pointer' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonIcon icon={addCircleOutline} class="icon" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                    <IonCardContent class="icon-text-main" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        Skapa ny router
                                    </IonCardContent>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                        <IonCol size="6" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard onClick={() => { setScannerMode('pump'); handleActivateScanner(); }} className="home-card" style={{ cursor: 'pointer' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonIcon icon={constructOutline} class="icon" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                    <IonCardContent class="icon-text-main" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        Konfigurera ny pump
                                    </IonCardContent>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                        <IonCol size="6" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard routerLink="/PumpCustomers" className="home-card" style={{ cursor: 'pointer' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonIcon icon={peopleOutline} class="icon" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                    <IonCardContent class="icon-text-main" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        Pumpkunder
                                    </IonCardContent>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                        <IonCol size="6" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard onClick={() => setShowCustomerModal(true)} className="home-card" style={{ cursor: 'pointer' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonIcon icon={personAddOutline} class="icon" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                    <IonCardContent class="icon-text-main" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        Skapa ny pumpkund
                                    </IonCardContent>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                        <IonCol size="6" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonCard onClick={() => setShowSearchModal(true)} className="home-card" style={{ cursor: 'pointer' }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonCardContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonIcon icon={searchOutline} class="icon" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                                    <IonCardContent class="icon-text-main" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        Sök pump
                                    </IonCardContent>
                                </IonCardContent>
                            </IonCard>
                        </IonCol>
                    </IonRow>
                </IonGrid>

                <IonModal isOpen={showPumpModal} onDidDismiss={() => setShowPumpModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Konfigurera ny pump</IonTitle>
                            <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonButton onClick={() => setShowPumpModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Avbryt</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent className="ion-padding" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>mqttID (Skannat)</IonLabel>
                            <IonInput value={pumpData.mqttID} disabled placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Namn</IonLabel>
                            <IonInput value={pumpData.name} onIonChange={e => setPumpData({ ...pumpData, name: e.detail.value! })} placeholder="Ange namn" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="stacked" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>System</IonLabel>
                            <IonSelect
                                value={pumpData.system}
                                placeholder="Välj system"
                                onIonChange={e => setPumpData({ ...pumpData, system: e.detail.value! })}
                                onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                            >
                                <IonSelectOption value="1" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Siemens Skåp</IonSelectOption>
                                <IonSelectOption value="2" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Direkt Frekvensare</IonSelectOption>
                                <IonSelectOption value="3" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Direkt Mjukstart</IonSelectOption>
                            </IonSelect>
                        </IonItem>
                        <div style={{ marginTop: '20px' }}>
                            <IonButton expand="block" onClick={handleCreatePump} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                Skapa pump
                            </IonButton>
                        </div>
                    </IonContent>
                </IonModal>

                <IonModal isOpen={showCustomerModal} onDidDismiss={() => setShowCustomerModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Skapa ny pumpkund</IonTitle>
                            <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonButton onClick={() => setShowCustomerModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Avbryt</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent className="ion-padding" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Namn</IonLabel>
                            <IonInput value={customerData.name} onIonChange={e => setCustomerData({ ...customerData, name: e.detail.value! })} placeholder="Ange namn" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Email</IonLabel>
                            <IonInput value={customerData.email} onIonChange={e => setCustomerData({ ...customerData, email: e.detail.value! })} placeholder="Ange email" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Företagsnamn</IonLabel>
                            <IonInput value={customerData.company_name} onIonChange={e => setCustomerData({ ...customerData, company_name: e.detail.value! })} placeholder="Ange företagsnamn" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Adress</IonLabel>
                            <IonInput value={customerData.address} onIonChange={e => setCustomerData({ ...customerData, address: e.detail.value! })} placeholder="Ange adress" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Postnummer</IonLabel>
                            <IonInput value={customerData.zip} onIonChange={e => setCustomerData({ ...customerData, zip: e.detail.value! })} placeholder="Ange postnummer" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Postort</IonLabel>
                            <IonInput value={customerData.city} onIonChange={e => setCustomerData({ ...customerData, city: e.detail.value! })} placeholder="Ange postort" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Land</IonLabel>
                            <IonInput value={customerData.country} onIonChange={e => setCustomerData({ ...customerData, country: e.detail.value! })} placeholder="Ange land" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Organisationsnummer</IonLabel>
                            <IonInput value={customerData.org_number} onIonChange={e => setCustomerData({ ...customerData, org_number: e.detail.value! })} placeholder="Ange organisationsnummer" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonLabel position="floating" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Mobilnummer</IonLabel>
                            <IonInput value={customerData.mobile} onIonChange={e => setCustomerData({ ...customerData, mobile: e.detail.value! })} placeholder="+46XXXXXXXXX" onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                        </IonItem>
                        <div style={{ marginTop: '20px', marginBottom: '40px' }}>
                            <IonButton expand="block" onClick={handleCreateCustomer} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                Skapa kund
                            </IonButton>
                        </div>
                    </IonContent>
                </IonModal>

                <IonModal isOpen={showSearchModal} onDidDismiss={() => setShowSearchModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                    <IonHeader placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonToolbar color="primary" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <IonTitle placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Sök pump</IonTitle>
                            <IonButtons slot="end" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <IonButton onClick={() => setShowSearchModal(false)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Stäng</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                        <IonSearchbar
                            onIonChange={(e: any) => handleSearch(e.detail.value!)}
                            placeholder="Sök på namn eller mqttID"
                            debounce={500}
                            animated={true}
                            onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                        />

                        {isSearching && (
                            <div className="ion-text-center ion-padding">
                                <IonSpinner placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined} />
                            </div>
                        )}

                        <IonList placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            {searchResults.map((pump) => (
                                <IonItem
                                    key={pump.id}
                                    button
                                    onClick={() => {
                                        setShowSearchModal(false);
                                        // Use pump.user.id if user is an object, or just pump.user if it's an ID
                                        const userId = pump.user?.id || pump.user;
                                        history.push(`/pump/${userId}/${pump.id}`);
                                    }}
                                    placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}
                                >
                                    <IonLabel placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                        <h2 style={{ fontWeight: 'bold' }}>{pump.name}</h2>
                                        <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.9em' }}>
                                            Kund: {pump.user?.company_name || pump.user?.name || 'Okänd'}
                                        </p>
                                        <p style={{ fontSize: '0.8em' }}>ID: {pump.mqtt_id}</p>
                                    </IonLabel>
                                </IonItem>
                            ))}
                            {!isSearching && (searchQuery || '').length >= 2 && searchResults.length === 0 && (
                                <IonItem placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                    <IonLabel color="medium" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>Inga pumpar hittades</IonLabel>
                                </IonItem>
                            )}
                        </IonList>
                    </IonContent>
                </IonModal>
            </IonContent>
        </IonPage>
    );
};

export default PumpsMenu;
